<template>
  <section class="section">
    <h2 class="title">
      {{ $t('route.apiChannels') }}
      <div class="level">
        <div class="level-left subtitle">
          {{ $t('subtitle.apiChannels') }}
        </div>
        <div class="level-right">
          <button class="button is-primary" @click.prevent="create">
            <octicon :icon="plus" /><span>{{ $t('newAPIChannel') }}</span>
          </button>
        </div>
      </div>
    </h2>
    <box>
      <data-table
        ref="table" :items="items" sort-by="_id"
        :theme="$_ui_theme_tables"
      >
        <data-column field="title" :label="$t('title')" width="15%" />
        <data-column field="description" :label="$t('description')" width="20%" />
        <data-column field="method" :label="$t('method')" width="8%" />
        <data-column field="url" :label="$t('url')" width="25%" />
        <data-column field="index_name" :label="$t('indexName')" width="12%" />
        <data-column field="schedule" :label="$t('schedule')" width="10%">
          <template slot-scope="{ value }">
            <span class="tag is-info">{{ formatSchedule(value) }}</span>
          </template>
        </data-column>
        <data-column field="count_log_send" :label="$t('countLogSend')" width="10%">
          <template slot-scope="{ value }">
            <span class="tag is-success">{{ formatNumber(value || 0) }}</span>
          </template>
        </data-column>
        <data-column
          :label="$t('actions')" class="has-text-centered" :sortable="false"
          width="10%"
        >
          <template slot-scope="props">
            <div class="has-text-centered">
              <button class="button is-text" @click.prevent="toggleStatus(props.item)" :title="props.item.enabled ? $t('deactivate') : $t('activate')">
                <octicon :icon="props.item.enabled ? stop : check" />
              </button>
              <button class="button is-text" @click.prevent="edit(props.item)" :title="$t('edit')">
                <octicon :icon="pencil" />
              </button>
              <button class="button is-text" @click.prevent="destroy(props.item)" :title="$t('delete')">
                <octicon :icon="x" />
              </button>
            </div>
          </template>
        </data-column>
      </data-table>
    </box>

    <modal v-if="item" :show.sync="modal" class="api-channel-modal">
      <box>
        <div slot="header">
          {{ item.title || $t('newAPIChannel') }}
        </div>

        <form @submit.prevent="submit">
          <!-- Tabs -->
          <div class="os-tabs">
            <div class="tab-headers">
              <div
                class="tab-header"
                :class="{ 'active': activeTab === 'config' }"
                @click="activeTab = 'config'"
              >
                <octicon :icon="gear" />
                <span>{{ $t('apiChannel.tabConfig') }}</span>
              </div>
              <div
                class="tab-header"
                :class="{ 'active': activeTab === 'payload' }"
                @click="activeTab = 'payload'"
              >
                <octicon :icon="code" />
                <span>{{ $t('apiChannel.tabPayload') }}</span>
              </div>
            </div>
          </div>

          <!-- Tab 1: Configuration -->
          <div v-show="activeTab === 'config'" class="tab-content">
            <!-- Basic Info Section -->
            <div class="config-section">
              <h5 class="title is-6 section-title">{{ $t('apiChannel.stepBasicInfo') }}</h5>
              <div class="columns">
                <div class="column is-half">
                  <div class="field">
                    <label class="label">{{ $t('title') }} <span class="has-text-danger">*</span></label>
                    <div class="control">
                      <input v-model="item.title" class="input" type="text" autocomplete="off" required>
                    </div>
                  </div>
                </div>
                <div class="column is-half">
                  <div class="field">
                    <label class="label">{{ $t('description') }}</label>
                    <div class="control">
                      <input v-model="item.description" class="input" type="text" autocomplete="off">
                    </div>
                  </div>
                </div>
              </div>

              <div class="field">
                <label class="label">{{ $t('index') }} <span class="has-text-danger">*</span></label>
                <div class="control">
                  <span class="select is-fullwidth">
                    <select v-model="item.index_id" required @change="loadIndexFields">
                      <option disabled value="">{{ $t('selectDefault') }}</option>
                      <option v-for="e in indexes" :key="e.id" :value="e.id">{{ e.name }}</option>
                    </select>
                  </span>
                </div>
              </div>

              <div class="field">
                <label class="label">{{ $t('apiChannel.query') }}</label>
                <div class="control">
                  <input v-model="item.query" class="input" type="text" :placeholder="$t('apiChannel.queryPlaceholder')" autocomplete="off">
                </div>
                <p class="help">{{ $t('apiChannel.queryHelp') }}</p>
              </div>

              <div class="field">
                <label class="label">{{ $t('apiChannel.responseIndex') }}</label>
                <div class="control">
                  <span class="select is-fullwidth">
                    <select v-model="item.response_index_id">
                      <option value="">{{ $t('apiChannel.noResponseIndex') }}</option>
                      <option v-for="e in indexes" :key="e.id" :value="e.id">{{ e.name }}</option>
                    </select>
                  </span>
                </div>
                <p class="help">{{ $t('apiChannel.responseIndexHelp') }}</p>
              </div>
            </div>

            <!-- HTTP Config Section -->
            <div class="config-section">
              <h5 class="title is-6 section-title">{{ $t('apiChannel.httpConfig') }}</h5>
              <div class="columns">
                <div class="column is-3">
                  <div class="field">
                    <label class="label">{{ $t('method') }} <span class="has-text-danger">*</span></label>
                    <div class="control">
                      <span class="select is-fullwidth">
                        <select v-model="item.method" required>
                          <option disabled value="">{{ $t('selectDefault') }}</option>
                          <option v-for="e in verbs" :key="e" :value="e">{{ e }}</option>
                        </select>
                      </span>
                    </div>
                  </div>
                </div>
                <div class="column is-9">
                  <div class="field">
                    <label class="label">{{ $t('url') }} <span class="has-text-danger">*</span></label>
                    <div class="control">
                      <input v-model="item.url" class="input" type="text" autocomplete="off" required placeholder="https://api.example.com/logs">
                    </div>
                  </div>
                </div>
              </div>

              <div class="field">
                <label class="label">{{ $t('headers') }}</label>
                <div class="field is-grouped">
                  <p class="control is-expanded">
                    <input v-model="header.key" class="input is-small" type="text" :placeholder="$t('key')" autocomplete="off">
                  </p>
                  <p class="control is-expanded">
                    <input v-model="header.value" class="input is-small" type="text" :placeholder="$t('value')" autocomplete="off">
                  </p>
                  <p class="control">
                    <button type="button" class="button is-info is-small" @click.prevent="addHeader">{{ $t('add') }}</button>
                  </p>
                </div>
                <div v-for="(h, index) in item.headers" :key="index" class="field is-grouped">
                  <p class="control is-expanded">
                    <input v-model="h.key" class="input is-small" type="text" :placeholder="$t('key')" autocomplete="off">
                  </p>
                  <p class="control is-expanded">
                    <input v-model="h.value" class="input is-small" type="text" :placeholder="$t('value')" autocomplete="off">
                  </p>
                  <p class="control">
                    <button type="button" class="button is-danger is-small" @click.prevent="removeHeader(index)">
                      <octicon :icon="x" />
                    </button>
                  </p>
                </div>
              </div>
            </div>

            <!-- Send Condition Section -->
            <div class="config-section">
              <schedule-selector v-model="item.schedule" />
              <query-test-section :index-id="item.index_id" :query="item.query" :schedule="item.schedule" />
            </div>
          </div>

          <!-- Tab 2: Payload Mapping -->
          <div v-show="activeTab === 'payload'" class="tab-content">
            <payload-mapping
              v-model="item.payload_fields"
              :index-fields="indexFields"
            />
          </div>

          <!-- Footer Buttons -->
          <div class="form-footer">
            <div class="buttons is-right">
              <button type="submit" class="button is-success">
                <octicon :icon="check" /> {{ $t('save') }}
              </button>
              <button type="button" class="button" @click.prevent="closeModal">
                {{ $t('cancel') }}
              </button>
            </div>
          </div>
        </form>
      </box>
    </modal>
  </section>
</template>

<script>
import { DataTable, DataColumn, Modal } from '@cyradar/ui'
import { plus, x, pencil, check, gear, code, stop } from 'octicons-vue'
import ScheduleSelector from '@/components/api-channels/ScheduleSelector'
import QueryTestSection from '@/components/api-channels/QueryTestSection'
import PayloadMapping from '@/components/api-channels/PayloadMapping'

const verbs = ['HEAD', 'GET', 'POST', 'PUT', 'PATCH', 'DELETE']

export default {
  components: {
    DataTable,
    DataColumn,
    Modal,
    ScheduleSelector,
    QueryTestSection,
    PayloadMapping
  },
  data () {
    return {
      modal: false,
      item: null,
      header: { key: '', value: '' },
      indexes: [],
      indexFields: [],
      activeTab: 'config'
    }
  },
  computed: {
    plus () { return plus },
    x () { return x },
    pencil () { return pencil },
    check () { return check },
    stop () { return stop },
    gear () { return gear },
    code () { return code },
    verbs () { return verbs }
  },
  mounted () {
    this.loadIndexes()
  },
  methods: {
    closeModal () {
      this.modal = false
      this.activeTab = 'config'
    },
    submit () {
      if (this.item.id) {
        this.update()
      } else {
        this.store()
      }
    },
    create () {
      this.item = {
        title: '',
        description: '',
        index_id: '',
        response_index_id: '',
        method: 'POST',
        url: '',
        headers: [],
        query: '',
        schedule: '5m',
        payload_fields: [],
        enabled: true
      }
      this.indexFields = []
      this.activeTab = 'config'
      this.modal = true
    },
    edit (item) {
      this.item = JSON.parse(JSON.stringify(item))
      if (!this.item.headers) this.item.headers = []
      if (!this.item.payload_fields) this.item.payload_fields = []
      if (!this.item.schedule) this.item.schedule = '5m'
      if (!this.item.response_index_id) this.item.response_index_id = ''
      if (!this.item.query) this.item.query = ''

      if (this.item.index_id) {
        this.loadIndexFields()
      }
      this.activeTab = 'config'
      this.modal = true
    },
    addHeader () {
      if (!this.item.headers) this.item.headers = []
      if (!this.header.key) return
      this.item.headers.push({ ...this.header })
      this.header = { key: '', value: '' }
    },
    removeHeader (idx) {
      if (!this.item.headers) this.item.headers = []
      this.item.headers.splice(idx, 1)
    },
    items (filter, order, pagination) {
      return this.$http.get('/api/v1/api-channels', {
        params: {
          filter: filter.query,
          sort: order.by,
          order: order.order,
          page: pagination.page,
          limit: pagination.perPage
        }
      }).then(body => {
        if (!body || !body.data) return {}
        return body.data.data
      })
    },
    store () {
      return this.$http.post('/api/v1/api-channels', this.item).then(body => {
        if (!body || !body.data) return
        this.closeModal()
        this.$store.dispatch('NOTIFY', { type: 'success', text: body.data.message })
        this.$refs.table.loadItems()
      })
    },
    update () {
      return this.$http.patch(`/api/v1/api-channels/${this.item.id}`, this.item).then(body => {
        if (!body || !body.data) return
        this.closeModal()
        this.$store.dispatch('NOTIFY', { type: 'success', text: body.data.message })
        this.$refs.table.loadItems()
      })
    },
    destroy (item) {
      if (!window.confirm(this.$t('rus'))) return
      return this.$http.delete(`/api/v1/api-channels/${item.id}`).then(body => {
        if (!body || !body.data) return
        this.$store.dispatch('NOTIFY', { type: 'success', text: body.data.message })
        this.$refs.table.loadItems()
      })
    },
    toggleStatus (item) {
      const newItem = { ...item, enabled: !item.enabled }
      return this.$http.patch(`/api/v1/api-channels/${item.id}`, newItem).then(body => {
        if (!body || !body.data) return
        this.$store.dispatch('NOTIFY', { type: 'success', text: body.data.message })
        this.$refs.table.loadItems()
      })
    },
    loadIndexes () {
      return this.$http.get('/api/v1/indexes/names').then(body => {
        if (!body || !body.data || !body.data.data) return
        this.indexes = body.data.data
      })
    },
    loadIndexFields () {
      if (!this.item.index_id) {
        this.indexFields = []
        return
      }
      return this.$http.get(`/api/v1/api-channels/index-fields/${this.item.index_id}`).then(body => {
        if (!body || !body.data || !body.data.data) {
          this.indexFields = []
          return
        }
        this.indexFields = body.data.data.fields || []
      }).catch(() => {
        this.indexFields = []
      })
    },
    formatSchedule (schedule) {
      const scheduleLabels = {
        '30s': '30 ' + this.$t('apiChannel.seconds'),
        '1m': '1 ' + this.$t('apiChannel.minute'),
        '5m': '5 ' + this.$t('apiChannel.minutes'),
        '15m': '15 ' + this.$t('apiChannel.minutes'),
        '30m': '30 ' + this.$t('apiChannel.minutes'),
        '1h': '1 ' + this.$t('apiChannel.hour'),
        '6h': '6 ' + this.$t('apiChannel.hours'),
        '12h': '12 ' + this.$t('apiChannel.hours'),
        '24h': '24 ' + this.$t('apiChannel.hours')
      }
      return scheduleLabels[schedule] || schedule
    },
    formatNumber (value) {
      if (value === null || value === undefined) return '0'
      return value.toLocaleString()
    }
  }
}
</script>

<style lang="scss" scoped>
.api-channel-modal {
  .box {
    max-width: 900px;
    width: 100%;
  }
}

.os-tabs {
  .tab-headers {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;
    border-bottom: 2px solid rgba(255, 255, 255, 0.1);
  }

  .tab-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 500;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;
    transition: all 0.2s;

    &:hover {
      color: rgba(255, 255, 255, 0.8);
    }

    &.active {
      color: #3273dc;
      border-bottom-color: #3273dc;
    }

    .octicon {
      width: 16px;
      height: 16px;
    }
  }
}

.tab-content {
  min-height: 400px;
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
}

.config-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
}

.section-title {
  color: inherit;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #3273dc;
  display: inline-block;
}

.form-footer {
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 1.5rem;
}
</style>
